// --- src/data/m3/Unit6.js ---

export const M3_UNIT_6 = {
  id: "m3-u6-hobbies-present-simple",
  title: "Unit 6: Hobbies & Present Simple",
  topic: "Hobbies vocabulary with present simple",
  iconType: "palette",
  grammarImage: "/m3-u6-hobbies.jpg",
  grammarText: "Learn hobby vocabulary and practice present simple: I like painting, she watches movies, do you read? Perfect for talking about what you do in your free time.",
  sets: [

    // ===================================================================
    // SET 1: HOBBIES VOCABULARY (A2)
    // ===================================================================
    {
      id: "m3-u6-set1",
      title: "Set 1: Hobby Vocabulary 🎨",
      context: `*Lunes por la mañana - Café en Sidney*

Laura es una artista que vive en Sidney. Hoy habla con amigos sobre pasatiempos.
"Me gusta pintar, leer libros y escuchar música" dice mientras toma café.
Sus amigos comparten sus propios hobbies.

*Monday morning - Café in Sidney*

Laura is an artist who lives in Toronto. Today she talks with friends about hobbies.
"I like painting, reading books, and listening to music" she says while drinking coffee.
Her friends share their own hobbies.`,
      
      questions: [
        {
          type: "match",
          q: "Match the hobby to the verb.",
          pairs: [
            { left: "Read 📚", right: "books" },
            { left: "Watch 🎬", right: "movies" },
            { left: "Listen to 🎵", right: "music" }
          ],
          a: "completed",
          tip: "Different verbs for different hobbies!",
          successTip: "Listen always needs TO! 🎯"
        },
        {
          type: "mc",
          q: "Laura says: 'I ___ books every night.'",
          options: ["read", "watch", "listen"],
          a: "read",
          tip: "READ books!"
        },
        {
          type: "input",
          q: "Laura's friend: 'She ___ movies on weekends.'",
          a: "watches",
          alts: ["watch"],
          tip: "Third person = watches!",
          successTip: "UK = See a film US = Watch a movie 🎬"
        },
        {
          type: "order",
          q: "Traduce: 'Escucho música todos los días.'",
          words: ["I", "listen", "to", "music", "every", "day."],
          a: "I listen to music every day.",
          extra: ["listens", "the", "musics"],
          tip: "Listen TO music!"
        },
        {
          type: "mc",
          q: "Laura asks: 'Do you ___ movies?'",
          options: ["watch", "watches", "see"],
          a: "watch",
          tip: "Do you + base form!",
          successTip: "Do you watch! Question form! ❓"
        },
        {
          type: "input",
          q: "Complete: 'He ___ to podcasts.🎧'",
          a: "listens",
          alts: ["listen"],
          tip: "Third person = listens!",
          successTip: "Hear es percibir un sonido; listen es prestar atención a lo que oyes"
        },
        {
          type: "match",
          q: "Match more hobbies to verbs.",
          pairs: [
            { left: "Take 📷", right: "photos" },
            { left: "Cook 🍳", right: "food" },
            { left: "Draw ✏️", right: "pictures" }
          ],
          a: "completed",
          successTip: "Photo es una imagen tomada con una cámara; picture es cualquier imagen! 🎨"
        },
        {
          type: "order",
          q: "Traduce: 'Ella cocina comida italiana.'",
          words: ["She", "cooks", "Italian", "food."],
          a: "She cooks Italian food.",
          extra: ["cook", "the", "foods"],
          tip: "She cooks!",
          successTip: "Food = cualquier cosa que se puede comer. Meal = desayuno, comida, cena.! 🍳"
        },
        {
          type: "mc",
          q: "Laura: 'My brother ___ photos every day.'",
          options: ["takes", "take", "make", "makes"],
          a: "takes",
          tip: "Third person = takes!",
        },
        {
          type: "input",
          q: "Complete: 'They ___ pictures on weekends.'",
          a: "draw",
          alts: ["draws"],
          tip: "They = base form!",
          successTip: "Usa estas palabras 'sketch' = un dibujo rápido, borrador o bosquejo. 'Drawing' = un dibujo más elaborado o terminado. ✏️"
        }
      ]
    },

    // ===================================================================
    // SET 2: LIKE/LOVE/HATE (A2)
    // ===================================================================
    {
      id: "m3-u6-set2",
      title: "Set 2: Like, Love, Hate 😊😍😡",
      context: `*Martes por la tarde - Estudio de Laura*

Laura muestra su estudio a visitantes.
"Me gusta pintar y tambien leer, pero odio limpiar mi estudio!"
Los visitantes ríen y comparten sus propias preferencias.

*Tuesday afternoon - Laura's studio*

Laura shows her studio to visitors.
"I love painting and reading too, but I hate cleaning my studio!"
Visitors laugh and share their own preferences.`,
      
      questions: [
        {
          type: "match",
          q: "Match the feeling to the verb.",
          pairs: [
            { left: "😍 (amo)", right: "love" },
            { left: "😊 (me gusta)", right: "like" },
            { left: "😡 (odio)", right: "hate" }
          ],
          a: "completed",
          successTip: "Love, like, hate! Basic feelings! 💭"
        },
        {
          type: "mc",
          q: "Laura: 'I ___ painting.'",
          options: ["love", "loves", "lovs"],
          a: "love",
        },
        {
          type: "input",
          q: "Complete: 'She ___ reading books.😊'",
          a: "likes",
          alts: ["loves","enjoys"],
          tip: "Third person = likes!",

        },
        {
          type: "order",
          q: "Traduce: 'Odio limpiar la casa.'",
          words: ["I", "hate", "cleaning", "the", "house."],
          a: "I hate cleaning the house.",
          extra: ["hates", "clean", "to"],
          tip: "I hate + -ing!",
          successTip: "Usa +ing despues de hate, love, like cuando hablas de hobbies 😡"
        },
        {
          type: "mc",
          q: "Laura's friend: 'My sister ___ cooking.'",
          options: ["loves", "love", "lovs"],
          a: "loves",
          tip: "Third person = loves!",
          successTip: "Loves! Third person! 🍳"
        },
        {
          type: "input",
          q: "Complete: 'They ___ watching TV.😒😠'",
          a: "hate",
          alts: ["don't like","do not like"],
          tip: "They = base form!",
        },
        {
          type: "order",
          q: "Traduce: 'Me encanta escuchar música.'",
          words: ["I", "love", "listening", "to", "music."],
          a: "I love listening to music.",
          extra: ["loves", "listen", "the"],
          tip: "I love + -ing!"
        },
        {
          type: "mc",
          q: "Laura asks: 'Do you ___ cooking?'",
          options: ["like", "likes", "liking"],
          a: "like",
          tip: "Do you + base form!",
        },
        {
          type: "input",
          q: "Complete: 'He doesn't ___ reading.'❌😡'",
          a: "like",
          alts: ["enjoy"],
          tip: "Doesn't + base form!",
        },
        {
          type: "order",
          q: "Traduce: 'Ella no odia dibujar.'",
          words: ["She", "doesn't", "hate", "drawing."],
          a: "She doesn't hate drawing.",
          extra: ["don't", "hates", "draw"],
          tip: "Doesn't + base verb!",
          successTip: "Doesn't hate! Al usar doesn't + base verb! 🎨"
        }
      ]
    },

    // ===================================================================
    // SET 3: PRESENT SIMPLE QUESTIONS (A2)
    // ===================================================================
    {
      id: "m3-u6-set3",
      title: "Set 3: Do You...? Questions",
      context: `*Miércoles - Clase de arte*

Laura enseña una clase de arte. Hace preguntas a sus estudiantes.
"Te gusta pintar? Ella dibuja todos los días? Que ven?"
Los estudiantes practican hacer y responder preguntas.

*Wednesday - Art class*

Laura teaches an art class. She asks questions to her students.
"Do you like painting? Does she draw every day? What do they watch?"
Students practice asking and answering questions.`,
      
      questions: [
        {
          type: "match",
          q: "Match question to short answer.",
          pairs: [
            { left: "Do you like art?", right: "Yes, I do." },
            { left: "Does she paint?", right: "No, she doesn't." },
            { left: "Do they read?", right: "Yes, they do." }
          ],
          a: "completed",
        },
        {
          type: "mc",
          q: "Laura: '___ you like painting?'",
          options: ["Do", "Does", "Are"],
          a: "Do",
          tip: "Do you...?"
        },
        {
          type: "input",
          q: "Question: '___ she read books?'",
          a: "Does",
          alts: [],
          tip: "Third person = Does!",
        },
        {
          type: "order",
          q: "Traduce: '¿Ves películas?'",
          words: ["Do", "you", "watch", "movies?"],
          a: "Do you watch movies?",
          extra: ["Does", "watches", "Are"],
          tip: "Do you + verb?"
        },
        {
          type: "mc",
          q: "Laura asks: '___ they listen to music?'",
          options: ["Do", "Does", "Are"],
          a: "Do",
          tip: "They = Do!",
        },
        {
          type: "input",
          q: "Answer: 'Does he cook?' → 'Yes, he ___.'",
          a: "does",
          alts: [],
          tip: "Short answer: he does!",
          successTip: "He does! Short answer! ✅"
        },
        {
          type: "order",
          q: "Traduce: '¿Ella dibuja todos los días?'",
          words: ["Does", "she", "draw", "every", "day?"],
          a: "Does she draw every day?",
          extra: ["Do", "draws", "drawing"],
          tip: "Does she + verb?"
        },
        {
          type: "mc",
          q: "Answer: 'Do you like art?' → 'No, I ___.'",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          tip: "I don't!",
        },
        {
          type: "input",
          q: "Question: '___ hobbies do you have?'",
          a: "What",
          alts: ["which"],
          tip: "What hobbies = qué pasatiempos!",
          successTip: "You can use 'pastime' but is not common 🎯"
        },
        {
          type: "order",
          q: "Traduce: '¿Qué lees?'",
          words: ["What", "do", "you", "read?"],
          a: "What do you read?",
          extra: ["does", "reads", "are"],
          tip: "What do you + verb?",
          successTip: "What do you read? Information question! 📚"
        }
      ]
    },

    // ===================================================================
    // SET 4: READING PASSAGES - Daily Hobbies (A2)
    // ===================================================================
    {
      id: "m3-u6-set4",
      title: "Set 4: Reading - Daily Hobbies 📖",
      context: `*Jueves - Tiempo de lectura*

Laura y sus estudiantes leen historias cortas sobre pasatiempos diarios.
"Lee sobre diferentes personas y sus pasatiempos" dice Laura.
Cada historia muestra actividades diferentes.

*Thursday - Reading time*

Laura and her students read short stories about daily hobbies.
"Read about different people and their hobbies" says Laura.
Each story shows different activities.`,
      
      questions: [
        {
          type: "passage",
          passage: `**Tom's Morning**

My name is Tom. Every morning I read the newspaper with my coffee. I like reading about world news. My wife doesn't read newspapers. She watches the news on TV instead.`
        },
        {
          type: "mc",
          q: "What does Tom do every morning?",
          options: ["Reads the newspaper", "Watches TV", "Listens to the radio"],
          a: "Reads the newspaper",
          tip: "First sentence!",
          successTip: "✓ MORNING ROUTINE!\nReading with coffee is a classic habit! ☕📰"
        },
        {
          type: "input",
          q: "Does Tom's wife read newspapers?",
          a: "No",
          alts: ["no she doesn't", "no she does not"],
          tip: "Check the last sentence.",
          successTip: "✓ DIFFERENT PREFERENCES!\nShe prefers TV news! 📺"
        },
        {
          type: "passage",
          passage: `**Sara's Music**

Hi! I'm Sara and I love music. I listen to music every day while I work. My favorite type is jazz. I also play the piano on weekends. Music makes me happy!`
        },
        {
          type: "mc",
          q: "When does Sara listen to music?",
          options: ["While she works", "Only on weekends", "In the morning"],
          a: "While she works",
          tip: "Check when she listens.",
          successTip: "✓ WORK MUSIC!\nMusic helps many people focus! 🎵"
        },
        {
          type: "input",
          q: "What instrument does Sara play?",
          a: "piano",
          alts: ["the piano"],
          tip: "She plays the ___.",
        },
        {
          type: "passage",
          passage: `**Mike's Cooking**

I'm Mike. I cook dinner for my family every night. I don't like fast food, so I make healthy meals at home. My kids love my pasta! Cooking is relaxing for me.`
        },
        {
          type: "mc",
          q: "How often does Mike cook dinner?",
          options: ["Every night", "On weekends", "Sometimes"],
          a: "Every night",
          tip: "Frequency mentioned!",
          successTip: "✓ You can also say 'I make dinner every night'! 🍝"
        },
        {
          type: "input",
          q: "What type of food doesn't Mike like?",
          a: "fast food",
          alts: ["fast"],
          tip: "He doesn't like ___.",
          successTip: "✓ HEALTHY CHOICE!\nHome-cooked meals are better! 🥗"
        },
        {
          type: "passage",
          passage: `**Emma's Art**

My name is Emma. I draw pictures in my free time. I have a small art studio at home. I don't sell my art. I draw for fun! My favorite things to draw are flowers and trees.`
        },
        {
          type: "mc",
          q: "Where does Emma draw?",
          options: ["At home in her studio", "At school", "In a park"],
          a: "At home in her studio",
          tip: "Location mentioned!",
          successTip: "✓ HOME STUDIO!\nHer own creative space! 🎨"
        },
        {
          type: "input",
          q: "What does Emma like to draw?",
          a: "flowers and trees",
          alts: ["flowers", ,"and","trees", "nature"],
          tip: "Her favorite things!",
          successTip: "✓You can say my favorite or my number one! 🌸🌳"
        }
      ]
    },

    // ===================================================================
    // SET 5: NEGATIVES WITH HOBBIES (A2)
    // ===================================================================
    {
      id: "m3-u6-set5",
      title: "Set 5: Negatives - Don't/Doesn't",
      context: `*Viernes - Conversación sobre lo que NO nos gusta*

Laura y sus amigos hablan sobre actividades que NO disfrutan.
"No me gusta limpiar. Mi hermana no ve televisión. Nosotros no cocinamos comida rápida."
Es importante expresar lo que no te gusta también.

*Friday - Conversation about what we DON'T like*

Laura and her friends talk about activities they DON'T enjoy.
"I don't like cleaning. My sister doesn't watch TV. We don't cook fast food."
It's important to express what you don't like too.`,
      
      questions: [
        {
          type: "match",
          q: "Match affirmative to negative.",
          pairs: [
            { left: "I like art", right: "I don't like art" },
            { left: "She reads books", right: "She doesn't read books" },
            { left: "They watch TV", right: "They don't watch TV" }
          ],
          a: "completed",
          tip: "Don't/doesn't!",
        },
        {
          type: "mc",
          q: "Laura: 'I ___ like cooking.'",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          tip: "I don't!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ watch movies.❌'",
          a: "doesn't",
          alts: ["does not"],
          tip: "Third person = doesn't!",
        },
        {
          type: "order",
          q: "Traduce: 'No escucho música clásica.'",
          words: ["I", "don't", "listen", "to", "classical", "music."],
          a: "I don't listen to classical music.",
          extra: ["doesn't", "not", "listens"],
          tip: "I don't + base verb!"
        },
        {
          type: "mc",
          q: "Laura's friend: 'My brother ___ read books.📚❌'",
          options: ["doesn't", "don't", "not"],
          a: "doesn't",
          tip: "He doesn't!",
          successTip: "He's not into books! 📚❌"
        },
        {
          type: "input",
          q: "Complete: 'They ___ like painting.'🎨❌",
          a: "don't",
          alts: ["do not"],
          tip: "They = don't!",
          successTip: "Don't! Plural negative!"
        },
        {
          type: "order",
          q: "Traduce: 'Él no cocina comida italiana.'",
          words: ["He", "doesn't", "cook", "Italian", "food."],
          a: "He doesn't cook Italian food.",
          extra: ["don't", "cooks", "not"],
          tip: "Doesn't + base verb!"
        },
        {
          type: "mc",
          q: "Laura: 'We ___ watch TV on weekdays.'📺❌",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          tip: "We don't!",
        },
        {
          type: "input",
          q: "Complete: 'She ___ take photos.'📷❌",
          a: "doesn't",
          alts: [],
          tip: "She doesn't!",
          successTip: "You can say 'Snap photos' → más informal, como “sacar fotos rápido” "
        },
        {
          type: "order",
          q: "Traduce: 'No dibujamos todos los días.'",
          words: ["We", "don't", "draw", "every", "day."],
          a: "We don't draw every day.",
          extra: ["doesn't", "draws", "not"],
          tip: "We don't + verb!",
        }
      ]
    },

    // ===================================================================
    // SET 6: READING PASSAGES - Weekend Activities (A2)
    // ===================================================================
    {
      id: "m3-u6-set6",
      title: "Set 6: Reading - Weekends 🏖️",
      context: `*Sábado - Historias de fin de semana*

Laura comparte historias sobre cómo diferentes personas pasan sus fines de semana.
"Todos tienen diferentes hobbies de fin de semana" explica.
Los estudiantes leen con atención.

*Saturday - Weekend stories*

Laura shares stories about how different people spend their weekends.
"Everyone has different weekend hobbies" she explains.
Students read carefully.`,
      
      questions: [
        {
          type: "passage",
          passage: `**John's Saturdays**

On Saturdays, I don't work. I wake up late and read for two hours. In the afternoon, I watch old movies with my dog. I love classic films! On Saturday nights, I cook a special dinner.`
        },
        {
          type: "mc",
          q: "What does John do first on Saturday morning?",
          options: ["Reads for two hours", "Watches movies", "Cooks dinner"],
          a: "Reads for two hours",
          tip: "After waking up!",
        },
        {
          type: "input",
          q: "What type of movies does John love?",
          a: "classic films",
          alts: ["classic", "old movies", "old"],
          tip: "He loves ___.",
          successTip: "✓ FILM FAN!\nClassic films = old movies! 🎬"
        },
        {
          type: "passage",
          passage: `**Anna's Sundays**

Sundays are my creative days. I paint in the morning when the light is beautiful. I don't watch TV or use my phone. I just paint for three hours. In the evening, I listen to music and relax.`
        },
        {
          type: "mc",
          q: "When does Anna paint?",
          options: ["Sunday mornings", "Saturday afternoons", "Every evening"],
          a: "Sunday mornings",
          tip: "Check the time!",
        },
        {
          type: "input",
          q: "How long does Anna paint?",
          a: "three hours",
          alts: ["3 hours", "three", "3"],
          tip: "She paints for ___.",
        },
        {
          type: "passage",
          passage: `**The Martinez Family**

The Martinez family loves weekends together. They don't eat in restaurants. On Sundays, everyone helps cook a big meal. The kids make salad, mom cooks the main dish, and dad makes dessert. They eat and talk for hours!`
        },
        {
          type: "mc",
          q: "Where does the Martinez family eat on Sundays?",
          options: ["At home", "In restaurants", "At friends' houses"],
          a: "At home",
          tip: "They don't eat in ___.",
        },
        {
          type: "input",
          q: "Who makes dessert in the family?",
          a: "dad",
          alts: ["the dad", "father"],
          tip: "___ makes dessert.",
          successTip: "✓Everyone helps! Everybody → un poco más informal. Everyone → ligeramente más formal."
        },
        {
          type: "passage",
          passage: `**Lisa's Hobby Time**

I work all week, so weekends are for my hobbies. On Saturday morning, I go to the library and borrow new books. In the afternoon, I take photos in the park. On Sunday, I just read all day. I don't answer phone calls. Reading time is my special time!`
        },
        {
          type: "mc",
          q: "Where does Lisa go on Saturday morning?",
          options: ["The library", "The park", "The bookstore"],
          a: "The library",
          tip: "She borrows books from ___.",
          successTip: "✓ Borrow = Tomar prestado! 📚"
        },
        {
          type: "input",
          q: "What does Lisa do on Sunday? 📖",
          a: "she reads",
          alts: ["reads","read", "reads all day", "she read"],
          tip: "She ___ all day.",
        }
      ]
    },

    // ===================================================================
    // SET 7: MIXED PRACTICE (A2+)
    // ===================================================================
    {
      id: "m3-u6-set7",
      title: "Set 7: Mixed Practice 🎯",
      context: `*Domingo - Repaso general*

Laura repasa todo lo aprendido con sus estudiantes.
"Vamos a practicar todo: positivo, negativo, y preguntas!"
Los estudiantes mezclan todas las formas.

*Sunday - General review*

Laura reviews everything learned with her students.
"Let's practice everything: positive, negative, and questions!"
Students mix all the forms.`,
      
      questions: [
        {
          type: "match",
          q: "Match the sentence type.",
          pairs: [
            { left: "I like art", right: "Positive" },
            { left: "She doesn't read", right: "Negative" },
            { left: "Do you cook?", right: "Question" }
          ],
          a: "completed",
          successTip: "Positive, negative, question! All three! ✅"
        },
        {
          type: "order",
          q: "Traduce: 'Mi hermana pinta todos los domingos.'",
          words: ["My", "sister", "paints", "every", "Sunday."],
          a: "My sister paints every Sunday.",
          extra: ["paint", "saturday"],
          tip: "Third person = paints!"
        },
        {
          type: "mc",
          q: "Complete: '___ your brother like music?'",
          options: ["Does", "Do", "Is"],
          a: "Does",
          tip: "Your brother = he = Does!",
        },
        {
          type: "input",
          q: "Complete: 'We ___ movies every Friday.'",
          a: "watch",
          alts: ["see"],
          tip: "We = base form!",
        },
        {
          type: "order",
          q: "Traduce: '¿Lees libros en inglés?'",
          words: ["Do", "you", "read", "books", "in", "English?"],
          a: "Do you read books in English?",
          extra: ["Does", "reads", "Are"],
          tip: "Do you + base verb?"
        },
        {
          type: "mc",
          q: "Complete: 'She ___ like cleaning.'",
          options: ["doesn't", "don't", "not"],
          a: "doesn't",
          tip: "She doesn't!",
        },
        {
          type: "input",
          q: "Complete: 'They ___ to music every day.'🎧",
          a: "listen",
          alts: [],
          tip: "They = base form!",
        },
        {
          type: "order",
          q: "Traduce: 'No cocinamos comida rápida.'",
          words: ["We", "don't", "cook", "fast", "food."],
          a: "We don't cook fast food.",
          extra: ["doesn't", "cooks"],
          tip: "We don't + base verb!"
        },
        {
          type: "mc",
          q: "Answer: 'Does he draw?' → 'No, he ___.'❌",
          options: ["doesn't", "don't", "not"],
          a: "doesn't",
          tip: "He doesn't!",
        },
        {
          type: "input",
          q: "Complete: 'I ___ photos on weekends.'",
          a: "take",
          alts: ["snap"],
          tip: "I = base form!",
        }
      ]
    },

    // ===================================================================
    // SET 8: CONVERSATIONS (A2+)
    // ===================================================================
    {
      id: "m3-u6-set8",
      title: "Set 8: Natural Conversations 💬",
      context: `*Lunes siguiente - Conversaciones naturales*

Laura practica conversaciones completas con sus estudiantes.
"Tengamos conversaciones reales sobre pasatiempos!"
Las conversaciones fluyen naturalmente.

*Next Monday - Natural conversations*

Laura practices complete conversations with her students.
"Let's have real conversations about hobbies!"
Conversations flow naturally.`,
      
      questions: [
        {
          type: "match",
          q: "Match question to natural answer.",
          pairs: [
            { left: "What do you do in your free time?", right: "I read books and watch movies." },
            { left: "Do you like cooking?", right: "Yes, I love it!" },
            { left: "Does your sister paint?", right: "No, she doesn't. She draws." }
          ],
          a: "completed",
          tip: "Natural responses!",
          successTip: "Complete answers sound natural! 💬"
        },
        {
          type: "order",
          q: "Traduce: '¿Qué haces en tu tiempo libre?'",
          words: ["What", "do", "you", "do", "in", "your", "free", "time?"],
          a: "What do you do in your free time?",
          extra: ["does", "is", "are"],
          tip: "What do you do?"
        },
        {
          type: "mc",
          q: "A: 'Do you like music?' B: '___'",
          options: ["Yes, I love it!", "Yes, I am.", "I like."],
          a: "Yes, I love music!",
          tip: "Complete answer = natural!",
          successTip: "Usa object pronouns para acortar las respuestas! 🎵"
        },
        {
          type: "input",
          q: "A: 'What does your brother do?' B: 'He ___ books and ___ movies.'📚🎬",
          a: "reads watches",
          alts: ["reads, watches", "read watch"],
          tip: "Third person = -s!",
        },
        {
          type: "order",
          q: "Traduce: 'A mi hermana le encanta cocinar.'",
          words: ["My", "sister", "loves", "cooking."],
          a: "My sister loves cooking.",
          extra: ["love", "cook", "to"],
          tip: "She loves!",
          successTip: "Hablando de hobbies usa love +ing"
        },
        {
          type: "mc",
          q: "A: 'Does she paint?' B: '___'",
          options: ["Yes, she does.", "Yes, she is.", "Yes, she paints."],
          a: "Yes, she does.",
          tip: "Use auxiliars!",
        },
        {
          type: "input",
          q: "A: 'What do you like to do?' B: 'I like ___ books.'",
          a: "reading",
          alts: ["to read"],
          tip: "I like + -ing!",
          successTip: "Reading! Natural answer! 📖"
        },
        {
          type: "order",
          q: "Traduce: '¿Tu amigo escucha música?'",
          words: ["Does", "your", "friend", "listen", "to", "music?"],
          a: "Does your friend listen to music?",
          extra: ["Do", "listens", "listening"],
          tip: "Does your friend...?"
        },
        {
          type: "mc",
          q: "A: 'What don't you like?' B: '___'",
          options: ["I don't like cleaning.", "I don't like.", "I like cleaning."],
          a: "I don't like cleaning.",
          tip: "Complete sentence!",
        },
        {
          type: "input",
          q: "A: 'Do you and your sister cook?' B: 'Yes, ___ cook together.'",
          a: "we",
          alts: [],
          tip: "You and your sister = we!",
          successTip: "We! Plural pronoun! Together = juntos! 🍳"
        }
      ]
    },

    // ===================================================================
    // SET 9: WH-QUESTIONS (A2+)
    // ===================================================================
    {
      id: "m3-u6-set9",
      title: "Set 9: Wh- Questions ❓",
      context: `*Martes - Preguntas con What, Where, When*

Laura enseña preguntas de información con palabras WH.
"Que lees? Donde pinta? Cuando cocinan?"
Los estudiantes aprenden a hacer preguntas específicas.

*Tuesday - Questions with What, Where, When*

Laura teaches information questions with WH words.
"What do you read? Where does she paint? When do they cook?"
Students learn to ask specific questions.`,
      
      questions: [
        {
          type: "match",
          q: "Match question word to purpose.",
          pairs: [
            { left: "What", right: "Ask about things" },
            { left: "Where", right: "Ask about places" },
            { left: "When", right: "Ask about time" }
          ],
          a: "completed",
          successTip: "What, where, when! Basic questions! ❓"
        },
        {
          type: "mc",
          q: "'___ do you read?'",
          options: ["What", "Where", "When"],
          a: "What",
          tip: "What = qué cosas!",
          successTip: "What! Ask about things! 📚"
        },
        {
          type: "input",
          q: "'___ does she paint?'",
          a: "Where",
          alts: ["what","why","when","how","how often","how much","how many","how long"],
          tip: "Where = dónde!",
          successTip: "Where! Ask about place! 🎨"
        },
        {
          type: "order",
          q: "Traduce: '¿Cuándo cocinas?'",
          words: ["When", "do", "you", "cook?"],
          a: "When do you cook?",
          extra: ["does", "are", "is"],
          tip: "When do you...?"
        },
        {
          type: "mc",
          q: "'___ books do you like?'",
          options: ["What", "Where", "When"],
          a: "What",
          tip: "What books = qué libros!",
          successTip: "Usa 'what kind of' o 'what type of' para ser mas especifico! 📖"
        },
        {
          type: "input",
          q: "'___ does he listen to music? -In the mornings'",
          a: "When",
          alts: [],
          tip: "When = cuándo!",
          successTip: "When! Ask about time! 🎵"
        },
        {
          type: "order",
          q: "Traduce: '¿Dónde ves películas?'",
          words: ["Where", "do", "you", "watch", "movies?"],
          a: "Where do you watch movies?",
          tip: "Where do you...?"
        },
        {
          type: "mc",
          q: "'___ does your sister draw?' - She draws a cat",
          options: ["What", "Where", "When"],
          a: "What",
          tip: "What = qué cosas!",
          successTip: "What! Things she draws! ✏️"
        },
        {
          type: "input",
          q: "'___ do they cook?' -At the new restaurant",
          a: "Where",
          alts: [],
          tip: "Where = dónde!",
          successTip: "Where! Ask about location! 🍳"
        },
        {
          type: "order",
          q: "Traduce: '¿Qué música escuchas?'",
          words: ["What","kind","of", "music", "do", "you", "listen", "to?"],
          a: "What kind of music do you listen to?",
          extra: ["does", "Where", "When"],
          tip: "What music do you...?",
          successTip: "You can also say 'what type of music do you listen to'! 🎧"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTER PRACTICE (B1-)
    // ===================================================================
    {
      id: "m3-u6-set10",
      title: "Set 10: Master All Forms 🏆",
      context: `*Miércoles - Evaluación final*

Laura evalúa el dominio completo de presente simple con hobbies.
"Ahora puedes hablar de hobbies usando todas las formas: positivas, negativas, y preguntas!"
Los estudiantes demuestran su dominio.

*Wednesday - Final evaluation*

Laura evaluates complete mastery of present simple with hobbies.
"You can now talk about hobbies using all forms: positive, negative, and questions!"
Students demonstrate their mastery.`,
      
      questions: [
        {
          type: "match",
          q: "Final review: Match forms.",
          pairs: [
            { left: "Positive", right: "I read books" },
            { left: "Negative", right: "She doesn't paint" },
            { left: "Question", right: "Do you cook?" }
          ],
          a: "completed",
          successTip: "All forms mastered! 🏆"
        },
        {
          type: "order",
          q: "Traduce: 'Leo libros, escucho música y veo películas.'",
          words: ["I", "read", "books,", "listen", "to", "music,", "and", "watch", "movies."],
          a: "I read books, listen to music, and watch movies.",
          extra: ["reads", "listens", "watches"],
          tip: "Three activities!",
          successTip: "Three hobbies in one sentence! Perfect! 💯"
        },
        {
          type: "mc",
          q: "Complete naturally: 'In my free time, I ___'",
          options: ["like reading and cooking", "love reads", "like do hobbies"],
          a: "like reading and cooking",
          tip: "Complete answer!",
          successTip: "Natural! Complete sentence! 📚🍳"
        },
        {
          type: "input",
          q: "Complete: 'My sister ___ books but she ___ watch TV.'📖❌📺",
          a: "reads doesn't",
          alts: ["reads, doesn't"],
          tip: "Third person + negative!",
        },
        {
          type: "order",
          q: "Traduce: '¿Qué hacen tus padres los domingos?'",
          words: ["What", "do", "your", "parents", "do", "on", "Sundays?"],
          a: "What do your parents do on Sundays?",
          extra: ["does", "doing", "are"],
          tip: "What do + plural?"
        },
        {
          type: "mc",
          q: "Best answer: 'What are your hobbies?'",
          options: ["I love painting and listening to music.", "I do painting.", "I have hobbies."],
          a: "I love painting and I like listening to music.",
          tip: "Detailed answer!",
          successTip: "Detailed! Native-level answer! 🎨🎵"
        },
        {
          type: "input",
          q: "Complete: 'Where ___ you usually read?'",
          a: "do",
          alts: [],
          tip: "Where do you...?",
          successTip: "Where do you! Information question! 📍"
        },
        {
          type: "order",
          q: "Traduce: 'Ella no cocina, pero le encanta comer.'",
          words: ["She", "doesn't", "cook,", "but", "she", "loves", "eating."],
          a: "She doesn't cook, but she loves eating.",
          extra: ["don't", "cooks", "eat"],
          tip: "But = contrast!",
          successTip: "Contrast! Doesn't cook BUT loves eating! 🍽️"
        },
        {
          type: "mc",
          q: "Complete: 'My friends and I ___ movies every Friday.'",
          options: ["watch", "watches", "read"],
          a: "watch",
          tip: "My friends and I = we!",
          successTip: "Watch! Plural form! Together! 🎬"
        },
        {
          type: "input",
          q: "Final! Your turn: 'I like ___ and I love ___.' (YOUR hobbies!)",
          a: "reading",
          alts: ["painting", "cooking", "drawing", "watching", "listening to music", "playing", "reading", "writing", "singing", "dancing","skiing","dancing"],
          tip: "Any hobbies!",
          successTip: "Awesome! You can talk about YOUR hobbies! Unit 6 complete! 🎉"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET: Laura's Creative Life
  // ===================================================================
  readingSet: {
    id: "m3-u6-reading",
    title: "Reading: Laura's Creative Life 🎨",
    category: "reading",

    questions: [
      {
        type: "passage",
        passage: `**Morning Routine**

Hi! I'm Laura and I'm an artist in Toronto. Every morning, I wake up at 7 AM and read the newspaper with my coffee. I don't watch TV in the morning. I like to start my day quietly with a book or the news.`
      },
      {
        type: "mc",
        q: "What time does Laura wake up?",
        options: ["7 AM", "8 AM", "6 AM"],
        a: "7 AM",
        tip: "Check the time!",
        successTip: "✓ EARLY RISER!\nLaura starts her day early! ☀️"
      },
      {
        type: "input",
        q: "Does Laura watch TV in the morning?",
        a: "No",
        alts: ["no she doesn't", "no she does not"],
        tip: "Check what she doesn't do.",
        successTip: "✓ QUIET MORNING!\nShe prefers reading! 📰"
      },
{
        type: "passage",
        passage: `**Working Hours**

I have a small art studio at home. I paint from 9 AM to 2 PM every day. I paint landscapes and portraits. While I work, I listen to classical music. Music helps me focus and be creative.`
      },
      {
        type: "mc",
        q: "How many hours does Laura paint each day?",
        options: ["5 hours", "3 hours", "7 hours"],
        a: "5 hours",
        tip: "9 AM to 2 PM = how many hours?",
        successTip: "✓ DEDICATED ARTIST!\n9 AM to 2 PM = 5 hours! 🎨"
      },
      {
        type: "input",
        q: "What does Laura listen to while she works?",
        a: "classical music",
        alts: ["music", "classical"],
        tip: "She listens to ___.",
        successTip: "✓ MUSIC HELPS!\nClassical music for focus! 🎵"
      },
      {
        type: "mc",
        q: "What does Laura paint?",
        options: ["Landscapes and portraits", "Only animals", "Abstract art"],
        a: "Landscapes and portraits",
        tip: "Two types mentioned!",
        successTip: "✓ TWO STYLES!\nLandscapes = scenery, Portraits = people! 🖼️"
      },

      {
        type: "passage",
        passage: `**After Work**

In the afternoons, I take a break from painting. I usually cook a healthy lunch. I don't eat fast food. After lunch, I sometimes go for a walk in the park to get fresh air and new ideas.`
      },
      {
        type: "input",
        q: "What type of food doesn't Laura eat?",
        a: "fast food",
        alts: ["fast"],
        tip: "She doesn't eat ___.",
        successTip: "✓ HEALTHY EATER!\nShe cooks at home! 🥗"
      },
      {
        type: "mc",
        q: "Why does Laura walk in the park?",
        options: ["To get fresh air and new ideas", "To meet friends", "To exercise only"],
        a: "To get fresh air and new ideas",
        tip: "Two reasons mentioned!",
        successTip: "✓ CREATIVE BREAK!\nFresh air = inspiration! 🌳💡"
      },

      {
        type: "passage",
        passage: `**Evening Time**

In the evenings, I love reading. I read books about art history and famous painters. I also read novels sometimes. Before I go to sleep, I write in my journal about my day and my new art ideas.`
      },
      {
        type: "mc",
        q: "What does Laura read about?",
        options: ["Art history and famous painters", "Only novels", "Science"],
        a: "Art history and famous painters",
        tip: "Her main reading topic!",
        successTip: "✓ ALWAYS LEARNING!\nShe studies art even in her free time! 📚"
      },
      {
        type: "input",
        q: "What does Laura write in before sleeping?",
        a: "journal",
        alts: ["her journal", "a journal"],
        tip: "She writes in her ___.",
        successTip: "✓ DAILY REFLECTION!\nJournal = diary! 📓"
      },
      {
        type: "passage",
        passage: `**Weekend Hobbies**

On weekends, I don't paint professionally. I do other creative things for fun! On Saturdays, I take photos in different parts of the city. On Sundays, I visit art galleries and museums to see other artists' work. I love my creative life!`
      },
      {
        type: "mc",
        q: "What does Laura do on Saturdays?",
        options: ["Takes photos in the city", "Visits museums", "Paints professionally"],
        a: "Takes photos in the city",
        tip: "Saturday activity!",
        successTip: "✓ COMPLETE!\nYou understand Laura's full routine! Great job! 🏆"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m3-u6-review",
    title: "Unit 6 Review",
    category: "review",
    
    questions: [
      {
        type: "mc",
        q: "Review: 'She ___ books every day.'",
        options: ["reads", "read", "reading"],
        a: "reads",
        tip: "Third person!"
      },
      {
        type: "input",
        q: "Review: 'I ___ like cooking.' (negative)",
        a: "don't",
        alts: [],
        tip: "I don't!"
      },
      {
        type: "mc",
        q: "Review: '___ you watch movies?'",
        options: ["Do", "Does", "Are"],
        a: "Do",
        tip: "Do you...?"
      },
      {
        type: "input",
        q: "Review: 'He ___ listen to music.' (negative)",
        a: "doesn't",
        alts: [],
        tip: "Doesn't!"
      },
      {
        type: "mc",
        q: "Review: '___ she paint?'",
        options: ["Does", "Do", "Is"],
        a: "Does",
        tip: "Third person question!"
      },
      {
        type: "input",
        q: "Review: 'They ___ photos.' (take)",
        a: "take",
        alts: [],
        tip: "Plural = base!"
      },
      {
        type: "mc",
        q: "Review: 'What ___ you read?'",
        options: ["do", "does", "are"],
        a: "do",
        tip: "What do you...?"
      },
      {
        type: "input",
        q: "Review: 'She ___ to music.' (listen)",
        a: "listens",
        alts: [],
        tip: "Third person!"
      },
      {
        type: "mc",
        q: "Review: 'Where ___ they cook?'",
        options: ["do", "does", "are"],
        a: "do",
        tip: "Where do they...?"
      },
      {
        type: "mc",
        q: "Review: Best answer to 'What are your hobbies?'",
        options: ["I love reading and I like painting.", "Reading.", "I have."],
        a: "I love reading and I like painting.",
        tip: "Complete answer!",
        successTip: "🏆 UNIT 6 COMPLETE! Present simple + hobbies mastered! Ready for Unit 7! 🚀"
      }
    ]
  }
};
